import { memo, useCallback, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import type { LayoutPerson } from './useFamilyTreeLayout';

// ─── Minimap (Übersichtskarte) ──────────────────────────────────────────────
//
// Zeigt den ganzen Baum stark verkleinert und darin den aktuell sichtbaren
// Ausschnitt. Klicken oder Ziehen verschiebt die Hauptansicht dorthin.

const MAP_W = 200;
const MAP_H = 132;
const PADDING = 6;
// Knotengröße in Baum-Koordinaten (entspricht PersonNode).
const NODE_W = 160;
const NODE_H = 90;

interface TreeMinimapProps {
  persons: LayoutPerson[];
  /** Aktuelle Zoom-/Pan-Transformation der Hauptansicht. */
  transform: { x: number; y: number; k: number };
  /** Größe des sichtbaren Canvas in Bildschirm-Pixeln. */
  viewport: { width: number; height: number };
  selectedPersonId: string | null;
  /** Ansicht auf diesen Punkt in Baum-Koordinaten zentrieren. */
  onNavigate: (treeX: number, treeY: number) => void;
  onClose: () => void;
}

export const TreeMinimap = memo(function TreeMinimap({
  persons,
  transform,
  viewport,
  selectedPersonId,
  onNavigate,
  onClose,
}: TreeMinimapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const draggingRef = useRef(false);

  // Baumgrenzen (Knotenkanten, nicht nur Mittelpunkte) und Maßstab.
  const view = useMemo(() => {
    if (persons.length === 0) return null;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const p of persons) {
      minX = Math.min(minX, p.x - NODE_W / 2);
      maxX = Math.max(maxX, p.x + NODE_W / 2);
      minY = Math.min(minY, p.y - NODE_H / 2);
      maxY = Math.max(maxY, p.y + NODE_H / 2);
    }
    const treeW = Math.max(maxX - minX, 1);
    const treeH = Math.max(maxY - minY, 1);
    const scale = Math.min((MAP_W - PADDING * 2) / treeW, (MAP_H - PADDING * 2) / treeH);
    // Verkleinerte Karte im Kästchen zentrieren.
    const offsetX = (MAP_W - treeW * scale) / 2;
    const offsetY = (MAP_H - treeH * scale) / 2;
    return { minX, minY, scale, offsetX, offsetY };
  }, [persons]);

  /** Baum-Koordinate → Minimap-Koordinate */
  const toMap = useCallback(
    (x: number, y: number) => {
      if (!view) return { x: 0, y: 0 };
      return {
        x: (x - view.minX) * view.scale + view.offsetX,
        y: (y - view.minY) * view.scale + view.offsetY,
      };
    },
    [view],
  );

  /** Mausposition auf der Minimap → Baum-Koordinate */
  const toTree = useCallback(
    (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg || !view) return null;
      const rect = svg.getBoundingClientRect();
      // Über das Verhältnis rechnen: die SVG wird per CSS skaliert dargestellt.
      const mapX = ((clientX - rect.left) / rect.width) * MAP_W;
      const mapY = ((clientY - rect.top) / rect.height) * MAP_H;
      return {
        x: (mapX - view.offsetX) / view.scale + view.minX,
        y: (mapY - view.offsetY) / view.scale + view.minY,
      };
    },
    [view],
  );

  const navigateToEvent = useCallback(
    (clientX: number, clientY: number) => {
      const pos = toTree(clientX, clientY);
      if (pos) onNavigate(pos.x, pos.y);
    },
    [toTree, onNavigate],
  );

  // Ziehen: Pointer Capture, damit die Bewegung auch außerhalb der Karte folgt.
  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    e.stopPropagation();
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    navigateToEvent(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!draggingRef.current) return;
    navigateToEvent(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    draggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  if (!view) return null;

  // Sichtbarer Ausschnitt in Baum-Koordinaten → Rechteck auf der Karte.
  const viewTopLeft = toMap(-transform.x / transform.k, -transform.y / transform.k);
  const viewW = (viewport.width / transform.k) * view.scale;
  const viewH = (viewport.height / transform.k) * view.scale;

  return (
    <div
      // Am Handy ausgeblendet: dort belegt die Toolbar die gesamte untere
      // Kante, und die verkleinerte Karte wäre kaum bedienbar.
      className="absolute z-[510] hidden overflow-hidden rounded-xl border border-slate-200 bg-white/95 shadow-lg backdrop-blur sm:block"
      // Über dem iOS-Home-Indicator halten, wie die Toolbar.
      style={{ right: '1rem', bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-2 py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          Übersicht
        </span>
        <button
          onClick={onClose}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label="Übersichtskarte schließen"
          title="Übersichtskarte schließen"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>

      <svg
        ref={svgRef}
        width={MAP_W}
        height={MAP_H}
        viewBox={`0 0 ${MAP_W} ${MAP_H}`}
        className="block cursor-crosshair touch-none"
        role="img"
        aria-label="Übersichtskarte des Stammbaums"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <rect width={MAP_W} height={MAP_H} fill="#f8fafc" />

        {/* Alle Personen als kleine Rechtecke */}
        {persons.map((p) => {
          const { x, y } = toMap(p.x, p.y);
          const w = Math.max(NODE_W * view.scale, 2);
          const h = Math.max(NODE_H * view.scale, 1.5);
          const isSelected = p.data.id === selectedPersonId;
          return (
            <rect
              key={p.data.id}
              x={x - w / 2}
              y={y - h / 2}
              width={w}
              height={h}
              rx={0.8}
              fill={isSelected ? '#2563eb' : '#cbd5e1'}
            />
          );
        })}

        {/* Sichtbarer Ausschnitt */}
        <rect
          x={viewTopLeft.x}
          y={viewTopLeft.y}
          width={viewW}
          height={viewH}
          fill="rgba(37, 99, 235, 0.08)"
          stroke="#2563eb"
          strokeWidth={1.2}
          rx={1.5}
          pointerEvents="none"
        />
      </svg>
    </div>
  );
});
